import React, { memo, useMemo, useState } from 'react'
import { Box, Button, Divider, Popover, useTheme } from '@mui/material'
import MultiStrikeColorPalettePicker from './MultiStrikeColorPalettePicker'
import LineStylePicker from '../../../../colorSelector/lineStylePicker/lineStylePicker'
import { ChartLineStyle, clampLineThickness, getLineDashProps, LineThickness } from '../../../../colorSelector/lineStyle.types'
import {
    MULTI_STRIKE_PALETTE_SIZE,
    MultiStrikeColorPaletteId,
    resolveMultiStrikeColorPalette,
} from '../../../multiStrikeColorPalettes'
import { useWidgetSettingsMenuPlacement } from '../../../../widgetDashboard/widgetSettingsMenuContext'

type Props = {
    colorPaletteId: MultiStrikeColorPaletteId | 'vivid' | 'soft'
    customCallBaseColor: string
    customPutBaseColor: string
    customCallHueShift: number
    customPutHueShift: number
    thickness?: number
    lineStyle?: ChartLineStyle
    onPaletteChange: (paletteId: MultiStrikeColorPaletteId) => void
    onCustomCallBaseColorChange: (color: string) => void
    onCustomPutBaseColorChange: (color: string) => void
    onCustomCallHueShiftChange: (hueShift: number) => void
    onCustomPutHueShiftChange: (hueShift: number) => void
    onThicknessChange: (thickness: LineThickness) => void
    onLineStyleChange: (style: ChartLineStyle) => void
}

const PreviewStrip = ({ colors }: { colors: string[] }) => (
    <Box sx={{ display: 'flex', gap: 0.25 }}>
        {colors.slice(0, MULTI_STRIKE_PALETTE_SIZE).map((color, index) => (
            <Box
                key={`${color}-${index}`}
                sx={{ width: 12, height: 7, borderRadius: 0.5, backgroundColor: color }}
            />
        ))}
    </Box>
)

const PreviewLine = ({
    color,
    thickness,
    lineStyle,
}: {
    color: string
    thickness: LineThickness
    lineStyle: ChartLineStyle
}) => {
    const { strokeDasharray, strokeLinecap } = getLineDashProps(lineStyle)
    return (
        <svg width="34" height="10" style={{ display: 'block', flexShrink: 0 }}>
            <line
                x1="2"
                y1="5"
                x2="32"
                y2="5"
                stroke={color}
                strokeWidth={thickness}
                strokeDasharray={strokeDasharray}
                strokeLinecap={strokeLinecap ?? 'butt'}
            />
        </svg>
    )
}

const MultiStrikeStrikeLines: React.FC<Props> = (props) => {
    const theme = useTheme()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const menuPlacement = useWidgetSettingsMenuPlacement('left')
    const open = Boolean(anchorEl)

    const palette = useMemo(
        () =>
            resolveMultiStrikeColorPalette(
                {
                    colorPaletteId: props.colorPaletteId,
                    customCallBaseColor: props.customCallBaseColor,
                    customPutBaseColor: props.customPutBaseColor,
                    customCallHueShift: props.customCallHueShift,
                    customPutHueShift: props.customPutHueShift,
                },
                theme.palette.mode === 'dark' ? 'dark' : 'light'
            ),
        [
            props.colorPaletteId,
            props.customCallBaseColor,
            props.customPutBaseColor,
            props.customCallHueShift,
            props.customPutHueShift,
            theme.palette.mode,
        ]
    )

    const lineThickness = clampLineThickness(props.thickness)
    const lineStyle = props.lineStyle ?? 'solid'

    return (
        <>
            <Button
                variant="outlined"
                color="inherit"
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{
                    minHeight: 34,
                    height: 34,
                    py: 0.5,
                    px: 1,
                    gap: 1,
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    borderColor: 'divider',
                    ...(open && {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: '1px',
                    }),
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <PreviewStrip colors={palette.callColors} />
                    <PreviewStrip colors={palette.putColors} />
                </Box>
                <PreviewLine color={palette.callColors[0]} thickness={lineThickness} lineStyle={lineStyle} />
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                disablePortal={menuPlacement.disablePortal}
                container={menuPlacement.container}
                sx={menuPlacement.overlaySx}
                anchorOrigin={menuPlacement.anchorOrigin}
                transformOrigin={menuPlacement.transformOrigin}
            >
                <Box sx={{ p: 1.5, width: 320, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <MultiStrikeColorPalettePicker
                        colorPaletteId={props.colorPaletteId}
                        customCallBaseColor={props.customCallBaseColor}
                        customPutBaseColor={props.customPutBaseColor}
                        customCallHueShift={props.customCallHueShift}
                        customPutHueShift={props.customPutHueShift}
                        onPaletteChange={props.onPaletteChange}
                        onCustomCallBaseColorChange={props.onCustomCallBaseColorChange}
                        onCustomPutBaseColorChange={props.onCustomPutBaseColorChange}
                        onCustomCallHueShiftChange={props.onCustomCallHueShiftChange}
                        onCustomPutHueShiftChange={props.onCustomPutHueShiftChange}
                    />
                    <Divider />
                    <LineStylePicker
                        lineThickness={lineThickness}
                        lineStyle={lineStyle}
                        onLineThicknessChange={props.onThicknessChange}
                        onLineStyleChange={props.onLineStyleChange}
                    />
                </Box>
            </Popover>
        </>
    )
}

export default memo(MultiStrikeStrikeLines)
